import {Component, inject, OnInit, ViewChild} from '@angular/core';
import cronstrue from 'cronstrue/i18n';
import {BaseComponentWithDrawer} from "../registry/base-component-with-drawer.component";
import {UserReport, UserReportListItem, UserReportMetric} from "../../models/report/UserReport";
import {GetUserReportsCB} from "../../ope/report/GetUserReportsCB";
import {PutUserReportCB} from "../../ope/report/PutUserReportCB";
import {DelUserReportCB} from "../../ope/report/DelUserReportCB";
import {UserReportOpeService} from "../../services/ope/user-report-ope.service";
import {UserReportFormComponent} from "./user-report-form/user-report-form.component";
import {UserOpeService} from "../../services/ope/userOpe.service";
import {GetUserMetricGroupsCB} from "../../ope/user/metric/GetUserMetricGroupsCB";
import {GetUserMetricGroupCB} from "../../ope/user/metric/GetUserMetricGroupCB";
import {AggregateFunctionTypeEnum} from "../../enums/dashboard/ChartsEnum";
import {ExecUserReportCB} from "../../ope/report/ExecUserReportCB";

@Component({
  selector: 'app-user-report',
  templateUrl: './user-report.component.html',
  styleUrls: ['./user-report.component.css', '../common/drawer-style.css', '../registry/base-component-with-drawer.component.css']
})
export class UserReportComponent extends BaseComponentWithDrawer implements OnInit {

  @ViewChild(UserReportFormComponent) reportForm?: UserReportFormComponent;

  protected isEditMode: boolean = false;
  protected isNewReport: boolean = false;
  protected userReports: UserReportListItem[] = [];
  protected selectedReport?: UserReport;
  protected reportTitle?: string;
  private savedReport?: UserReport;
  protected readonly userReportOpeService = inject(UserReportOpeService);
  private readonly  userOpeService = inject(UserOpeService);

  protected chartList: { id: number, name: string }[] = [];
  protected isImportPopupVisible: boolean = false;
  protected isImportLoading: boolean = false;
  protected selectedChartId?: number;
  protected importOptions: { metrics: boolean | null | undefined; bucket: boolean | null | undefined; name: boolean | null | undefined } = { metrics: true, bucket: true, name: false };

  protected cronToHuman(expression: string): string {
    try {
      return cronstrue.toString(expression, { locale: this.translate.currentLang ?? 'en' });
    } catch {
      return expression;
    }
  }

  protected metricsPreviewItems(metrics: UserReportMetric[]): string[] {
    return (metrics ?? []).slice(0, 5).map(m => {
      const device = m.metricInstance.deviceName;
      return device ? `${device} > ${m.metricInstance.name}` : m.metricInstance.name;
    });
  }

  protected metricsTooltipItems(metrics: UserReportMetric[]): string[] {
    return (metrics ?? []).map(m =>
      [m.metricInstance.partitionName, m.metricInstance.deviceName, m.metricInstance.name]
        .filter(Boolean).join(' > ')
    );
  }

  protected metricsHasMore(metrics: UserReportMetric[]): boolean {
    return (metrics?.length ?? 0) > 5;
  }

  ngOnInit() {
    super.ngOnInit();
    this.loadUserReports();
  }

  loadUserReports(): void {
    const cb = new GetUserReportsCB();
    this.userReportOpeService.getUserReports(cb).then(() => {
      this.userReports = cb.o.reports;
    });
  }

  private openReport(item: UserReportListItem, editMode: boolean): void {
    const report = new UserReport();
    report.id = item.id;
    report.name = item.name;
    report.email = item.email;
    report.unixCronExpression = item.unixCronExpression;
    report.enabled = item.enabled;
    report.bucketUnits = item.bucketUnits;
    report.bucketValue = item.bucketValue;
    report.metrics = (item.metrics ?? []).map(m => {
      const metric = new UserReportMetric();
      metric.uniqueCode = m.uniqueCode;
      metric.metricInstance = m.metricInstance as any;
      metric.functionType = m.functionType;
      return metric;
    });
    this.selectedReport = report;
    this.isDrawerOpen = true;
    if (editMode) {
      this.enterEditMode();
    }
  }

  private loadChartList(): void {
    const cb = new GetUserMetricGroupsCB();
    this.userOpeService.getUserMetricGroups(cb).then(() => {
      this.chartList = cb.o.groups;
    });
  }

  private enterEditMode(): void {
    this.savedReport = this.appService.deepCopy(this.selectedReport);
    this.isEditMode = true;
    this.loadChartList();
  }

  protected enableEditMode(): void {
    this.enterEditMode();
  }

  protected disableEditMode(): void {
    const revertOrClose = () => {
      if (this.isNewReport) {
        this.closeDrawer();
      } else {
        this.selectedReport = this.appService.deepCopy(this.savedReport);
        this.isEditMode = false;
      }
    };
    if (!this.appService.deepEquals(this.savedReport, this.selectedReport)) {
      this.showDiscardEditsDialog().show().done((result: boolean) => {
        if (result) revertOrClose();
      });
    } else {
      revertOrClose();
    }
  }

  protected openImportPopup(): void {
    this.selectedChartId = undefined;
    this.importOptions = { metrics: true, bucket: true, name: false };
    this.isImportPopupVisible = true;
  }

  protected confirmImport(): void {
    if (!this.selectedChartId) return;
    this.isImportLoading = true;
    const cb = new GetUserMetricGroupCB();
    cb.i.id = this.selectedChartId;
    this.userOpeService.getUserMetricGroup(cb).then(() => {
      const imported = this.appService.deepCopy(this.selectedReport) as UserReport;
      if (this.importOptions.name) {
        imported.name = cb.o.name;
      }
      if (this.importOptions.bucket) {
        imported.bucketUnits = cb.o.bucketUnits;
        imported.bucketValue = cb.o.bucketValue;
      }
      if (this.importOptions.metrics) {
        imported.metrics = (cb.o.metrics ?? []).map(m => {
          const metric = new UserReportMetric();
          metric.metricInstance = {
            id: m.metricInstance.id,
            name: m.metricInstance.name,
            type: m.metricInstance.type,
            partitionId: m.metricInstance.partitionId,
            partitionName: m.metricInstance.partitionName,
            deviceId: m.metricInstance.deviceId,
            deviceName: m.metricInstance.deviceName
          };
          metric.functionType = (m.functionType as AggregateFunctionTypeEnum) ?? AggregateFunctionTypeEnum.MEAN;
          return metric;
        });
      }
      this.selectedReport = imported;
      this.isImportPopupVisible = false;
      this.isImportLoading = false;
      this.selectedChartId = undefined;
    }).catch(() => {
      this.isImportLoading = false;
    });
  }

  protected saveUserReport(): void {
    if (!this.selectedReport) return;
    if (!this.reportForm?.validate()) return;

    const cb = new PutUserReportCB();
    cb.i.id = this.selectedReport.id;
    cb.i.name = this.selectedReport.name;
    cb.i.email = this.selectedReport.email;
    cb.i.unixCronExpression = this.selectedReport.unixCronExpression;
    cb.i.enabled = this.selectedReport.enabled;
    cb.i.bucketUnits = this.selectedReport.bucketUnits!;
    cb.i.bucketValue = this.selectedReport.bucketValue!;
    cb.i.metrics = (this.selectedReport.metrics ?? []).map(m => ({
      id: m.metricInstance.id,
      functionType: m.functionType
    }));

    this.userReportOpeService.putUserReport(cb).then(() => {
      this.toastService.openSuccessToastMessage(this.translate.instant('DASHBOARD.REPORTS.SAVED'));
      this.reportTitle = this.selectedReport?.name;
      this.isEditMode = false;
      this.isDrawerOpen = false;
      this.isNewReport = false;
      this.loadUserReports();
    }).catch(() => {});
  }

  protected deleteUserReport(): void {
    if (!this.selectedReport?.id) return;
    this.showDeleteDialog(this.selectedReport.name).show().done().then(async (dialogResult: any) => {
      if (dialogResult) {
        const cb = new DelUserReportCB();
        cb.i.id = this.selectedReport!.id!;
        this.userReportOpeService.delUserReport(cb).then(() => {
          this.toastService.openSuccessToastMessage(this.translate.instant('DASHBOARD.REPORTS.DELETED'));
          this.isDrawerOpen = false;
          this.loadUserReports();
        });
      }
    });
  }

  closeDrawer(): void {
    this.isDrawerOpen = false;
    this.isEditMode = false;
    this.isNewReport = false;
    this.selectedReport = undefined;
    this.savedReport = undefined;
    this.reportTitle = undefined;
    this.isImportPopupVisible = false;
    this.isImportLoading = false;
  }

  showNewReportForm(): void {
    this.isNewReport = true;
    this.selectedReport = new UserReport();
    this.isDrawerOpen = true;
    this.enterEditMode();
  }

  onRowClick(report: UserReportListItem): void {
    this.isNewReport = false;
    this.isEditMode = false;
    this.reportTitle = report.name;
    this.savedReport = undefined;
    this.openReport(report, false);
  }

  onEditReport($event: MouseEvent, report: UserReportListItem): void {
    $event.stopPropagation();
    this.isNewReport = false;
    this.reportTitle = report.name;
    this.savedReport = undefined;
    this.openReport(report, true);
  }

  onDeleteReport($event: MouseEvent, report: UserReportListItem): void {
    $event.stopPropagation();
    this.showDeleteDialog(report.name).show().done().then(async (dialogResult: any) => {
      if (dialogResult) {
        const cb = new DelUserReportCB();
        cb.i.id = report.id;
        this.userReportOpeService.delUserReport(cb).then(() => {
          this.toastService.openSuccessToastMessage(this.translate.instant('DASHBOARD.REPORTS.DELETED'));
          this.loadUserReports();
        });
      }
    });
  }

  onExecReport($event: MouseEvent, report: UserReportListItem): void {
    $event.stopPropagation();
    this.showExecDialog(report.name, report.email).show().done().then(async (dialogResult: any) => {
      if (dialogResult) {
        const cb = new ExecUserReportCB();
        cb.i.id = report.id!;
        this.userReportOpeService.execUserReport(cb).then(() => {
          this.toastService.openSuccessToastMessage(this.translate.instant('DASHBOARD.REPORTS.EXECUTED'));
        })
      }
    })
  }

  protected readonly document = document;
}
